import React, { useEffect, useState } from 'react';
import {
  AutoCompleteSelectUI,
  ButtonUI,
  CheckboxUI,
  DialogUI,
  FormErrorUI,
  FormLabelUI,
  InputUI,
  TimePickerUi,
} from '../Interface';
import { Field, useFormikContext } from 'formik';
import dayjs from 'dayjs';
import { v4 as uuidv4 } from 'uuid';
import { Box } from '@mui/material';
import { round } from 'mathjs';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import useConfig from '../../context/ConfigContext';
import useParam from '../../context/ParamContext';

const EmployeeAttendanceFieldsDialog = ({ onClose, dialogData, autocompleteOptions, defaultField }) => {
  const { open, index } = dialogData;
  const { config } = useConfig();
  const { params } = useParam();
  const { values, setFieldValue, errors, validateForm } = useFormikContext();
  const [lineData, setLineData] = useState({});

  const isEdit = index !== null && index !== undefined;

  useEffect(() => {
    if (open) {
      setLineData(
        isEdit
          ? values.employeeAttendanceLine[index]
          : {
              ...defaultField,
              fromTime: config.defTaskListReportFromTime,
              toTime: config.defTaskListReportToTime,
              timeSpent: calculateTimeSpent(config.defTaskListReportFromTime, config.defTaskListReportToTime),
            }
      );
    }
  }, [open, index]);

  const hasOverlap = (newLine, lines, currentIndex) => {
    return lines.some((line, i) => {
      if (i === currentIndex) return false;

      const sameEmployee = line.employeeId && newLine.employeeId && line.employeeId === newLine.employeeId;

      const sameCompany = line.companyId && newLine.companyId && line.companyId === newLine.companyId;

      if (!sameEmployee && !sameCompany) return false;

      if (line.absence || newLine.absence) return false;

      if (!line.fromTime || !line.toTime || !newLine.fromTime || !newLine.toTime) return false;

      const newFrom = dayjs.utc(newLine.fromTime);
      const newTo = dayjs.utc(newLine.toTime);
      const existingFrom = dayjs.utc(line.fromTime);
      const existingTo = dayjs.utc(line.toTime);

      return newFrom.isBefore(existingTo) && newTo.isAfter(existingFrom);
    });
  };

  const handleSave = async () => {
    const updatedLine = {
      ...lineData,
      id: lineData.id ?? uuidv4(),
    };

    let tempArray = [...values.employeeAttendanceLine];

    if (isEdit) {
      tempArray[index] = updatedLine;
    } else {
      tempArray.push(updatedLine);
    }

    const errors = await validateForm({
      ...values,
      employeeAttendanceLine: tempArray,
    });

    const rowIndex = isEdit ? index : tempArray.length - 1;

    if (hasOverlap(updatedLine, tempArray, rowIndex)) {
      addNotification({
        content: 'Az időintervallum átfed egy meglévő bejegyzéssel ennél a dolgozónál / alvállalkozónál.',
        type: NotificationType.WARNING,
      });
      return;
    }

    const rowErrors = errors?.employeeAttendanceLine?.[rowIndex];

    if (rowErrors) return;

    await setFieldValue('employeeAttendanceLine', tempArray);
    onClose();
  };

  const rowIndex = isEdit ? index : values.employeeAttendanceLine?.length;
  const rowErrors = errors?.employeeAttendanceLine?.[rowIndex];

  const calculateTimeSpent = (fromTime, toTime) => {
    if (!fromTime || !toTime) return null;

    const from = dayjs.utc(fromTime);
    const to = dayjs.utc(toTime);

    return round(to.diff(from, 'hour', true), 2);
  };

  const contractorsManagementEnabled = params.EMPLOYEE_ATTENDANCE_CONTRACTORS_MANAGEMENT === 'Y';

  return (
    <DialogUI
      open={open}
      headerContent="Dolgozói adatok"
      fullWidth={true}
      onClose={onClose}
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '800px',
            mx: 1,
          },
        },
        '& .MuiDialogContent-root': {
          px: 1,
          pb: 0,
        },
      }}
    >
      <div className="grid grid-cols-3 gap-x-4 gap-y-2 mx-4 mt-2">
        <div className="col-span-3 md:col-span-1">
          <AutoCompleteSelectUI
            fullWidth
            label={<FormLabelUI text="Dolgozó" />}
            value={autocompleteOptions.employees.find((e) => e.value === lineData?.employeeId) || null}
            onChange={(_e, newVal) =>
              setLineData((prev) => ({
                ...prev,
                employeeId: newVal?.value ?? null,
              }))
            }
            helperText={<FormErrorUI message={rowErrors?.employeeId} />}
            dataset={autocompleteOptions.employees}
            disabled={lineData?.companyId}
          />
        </div>
        {contractorsManagementEnabled && (
          <>
            <div className="col-span-3 md:col-span-1">
              <AutoCompleteSelectUI
                fullWidth
                label={<FormLabelUI text="Alvállalkozó" />}
                value={autocompleteOptions.company.find((e) => e.value === lineData?.companyId) || null}
                onChange={(_e, newVal) =>
                  setLineData((prev) => ({
                    ...prev,
                    companyId: newVal?.value ?? null,
                    headCount: newVal?.value ? 1 : '',
                  }))
                }
                helperText={<FormErrorUI message={rowErrors?.companyId} />}
                dataset={autocompleteOptions.company}
                disabled={lineData?.employeeId}
              />
            </div>
            <div className="col-span-3 md:col-span-1">
              <InputUI
                type="number"
                fullWidth
                label={<FormLabelUI text="Létszám" />}
                value={lineData?.headCount ?? ''}
                variant="standard"
                onChange={(e) =>
                  setLineData((prev) => ({
                    ...prev,
                    headCount: e.target.value,
                  }))
                }
                helperText={<FormErrorUI message={rowErrors?.headCount} />}
                disabled={!lineData?.companyId}
              />
            </div>
          </>
        )}
        <div className="col-span-3 md:col-span-1">
          <TimePickerUi
            fullWidth
            label={<FormLabelUI text="Kezdő időpont" />}
            value={lineData?.fromTime ? dayjs.utc(lineData?.fromTime) : null}
            timeSteps={{ minutes: 15 }}
            onChange={(value) => {
              if (!value) {
                setLineData((prev) => ({
                  ...prev,
                  fromTime: null,
                  timeSpent: null,
                }));
                return;
              }
              const iso = dayjs.utc(`1970-01-01T${value.format('HH:mm')}:00`).toISOString();

              const newTimeSpent = calculateTimeSpent(iso, lineData?.toTime);

              setLineData((prev) => ({
                ...prev,
                fromTime: iso,
                timeSpent: newTimeSpent,
              }));
            }}
            helperText={<FormErrorUI message={rowErrors?.fromTime} />}
          />
        </div>
        <div className="col-span-3 md:col-span-1">
          <TimePickerUi
            fullWidth
            label={<FormLabelUI text="Befejező időpont" />}
            value={lineData?.toTime ? dayjs.utc(lineData?.toTime) : null}
            timeSteps={{ minutes: 15 }}
            onChange={(value) => {
              if (!value) {
                setLineData((prev) => ({
                  ...prev,
                  toTime: null,
                  timeSpent: null,
                }));
                return;
              }

              const to = dayjs.utc(`1970-01-01T${value.format('HH:mm')}:00`);

              if (lineData?.fromTime) {
                const from = dayjs.utc(lineData?.fromTime);

                if (to.isBefore(from)) {
                  addNotification({
                    content: 'A befejező időpont nem lehet korábbi, mint a kezdő időpont!',
                    type: NotificationType.WARNING,
                  });

                  setLineData((prev) => ({
                    ...prev,
                    toTime: null,
                    timeSpent: '',
                  }));
                  return;
                }
              }

              const iso = to.toISOString();
              const newTimeSpent = calculateTimeSpent(lineData?.fromTime, iso);

              setLineData((prev) => ({
                ...prev,
                toTime: iso,
                timeSpent: newTimeSpent,
              }));
            }}
            helperText={<FormErrorUI message={rowErrors?.toTime} />}
          />
        </div>
        <div className="col-span-3 md:col-span-1">
          <InputUI
            type="number"
            fullWidth
            label={<FormLabelUI text="Idő (óra)" />}
            value={lineData?.timeSpent ?? ''}
            variant="standard"
            onChange={(e) =>
              setLineData((prev) => ({
                ...prev,
                timeSpent: e.target.value,
              }))
            }
            helperText={<FormErrorUI message={rowErrors?.timeSpent} />}
          />
        </div>
        {params.SHOW_EMPLOYEE_ATTENDANCE_TASKLIST_FIELD === 'Y' && (
          <div className="col-span-3 md:col-span-1">
            <AutoCompleteSelectUI
              fullWidth
              label={<FormLabelUI text="Munkanem" />}
              value={autocompleteOptions.taskList.find((e) => e.value === lineData?.taskListId) || null}
              onChange={(_e, newVal) =>
                setLineData((prev) => ({
                  ...prev,
                  taskListId: newVal?.value ?? null,
                }))
              }
              helperText={<FormErrorUI message={rowErrors?.taskListId} />}
              dataset={autocompleteOptions.taskList}
            />
          </div>
        )}
        {params.SHOW_EMPLOYEE_ATTENDANCE_RATING_FIELD === 'Y' && (
          <div className="col-span-3 md:col-span-1">
            <InputUI
              type="number"
              fullWidth
              label={<FormLabelUI text="Értékelés" />}
              value={lineData?.rating ?? ''}
              variant="standard"
              onChange={(e) =>
                setLineData((prev) => ({
                  ...prev,
                  rating: e.target.value,
                }))
              }
              helperText={<FormErrorUI message={rowErrors?.rating} />}
            />
          </div>
        )}
        {params.SHOW_EMPLOYEE_ATTENDANCE_ABSENCE_FIELD === 'Y' && (
          <div className="col-span-3 md:col-span-1 pt-2.5">
            <CheckboxUI
              label={<FormLabelUI text="Távollét" />}
              checked={lineData?.absence ?? false}
              onChange={(e) =>
                setLineData((prev) => ({
                  ...prev,
                  absence: e.target.checked,
                }))
              }
              helperText={<FormErrorUI message={rowErrors?.absence} />}
            />
          </div>
        )}
        <div className="col-span-3">
          <InputUI
            fullWidth
            label={<FormLabelUI text="Megjegyzés" />}
            value={lineData?.description ?? ''}
            variant="standard"
            onChange={(e) =>
              setLineData((prev) => ({
                ...prev,
                description: e.target.value,
              }))
            }
            helperText={<FormErrorUI message={rowErrors?.description} />}
            multiline
            rows={2}
          />
        </div>
      </div>

      <Box
        display="flex"
        justifyContent="center"
        gap={0.5}
        mt="1.5rem"
        position="sticky"
        bottom={0}
        backgroundColor="#fff"
      >
        <ButtonUI text="Mentés" className="bg-success" onClick={handleSave} />
        <ButtonUI text="Mégsem" className="bg-primary" onClick={onClose} />
      </Box>
    </DialogUI>
  );
};

export default EmployeeAttendanceFieldsDialog;
